import Table from '@shared/components/Table';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import NomenclaturesOzForm from './NomenclaturesOzForm';
import DeleteModal from '@shared/components/DeleteModal';
import ActionButtons from '@shared/components/ActionButtons';
import { COMMON_BUTTONS } from '@config/actionButtonConfigs';
import { nomenclaturesOz } from '@modules/organizationalStructure/nomenclatures/services/nomenclaturesOz';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';

export default function NomenclaturesOz() {
    const { t, i18n } = useTranslation();
    const [selectedData, setSelectedData] = useState(null);
    const [modalState, setModalState] = useState({ visible: false, type: null, editData: null });
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedNomenclature, setSelectedNomenclature] = useState('');
    const [nomenclaturesOptions, setNomenclaturesOptions] = useState([]);
    const [selectedActive, setSelectedActive] = useState('true'); // Default to active

    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchNomenclatures();
    }, []);

    useEffect(() => {
        if (selectedNomenclature) {
            fetchNomenclatureItems();
        } else {
            setTableData([]);
            setSelectedData(null);
        }
    }, [selectedNomenclature, selectedActive]);

    useEffect(() => {
        fetchNomenclatures();
        if (selectedNomenclature) {
            fetchNomenclatureItems();
        }
    }, [i18n.language]);

    const fetchNomenclatures = async () => {
        try {
            const nomenclatures = await nomenclaturesOz.getNomenclaturesOz();
            setNomenclaturesOptions(nomenclatures);
        } catch (error) {
            console.error('Error fetching nomenclatures:', error);
        }
    };

    const fetchNomenclatureItems = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const active = selectedActive === 'true';
            const items = await nomenclaturesOz.getNomenclatureItems(selectedNomenclature, active);

            const flattenedItems = items.map(item => ({
                ...item,
                code: item.nomenclatureItem?.code || '',
                active: item.nomenclatureItem?.active || false,
                isActive: item.nomenclatureItem?.active || false,
                nomenclatureItemLngs: item.nomenclatureItemLngs || []
            }));

            setTableData(flattenedItems);
        } catch (error) {
            console.error('❌ [fetchNomenclatureItems] Error fetching nomenclature items:', error);
            setError('Failed to load nomenclature items');
            setTableData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = async (type) => {
        if (type === 'add' && !selectedNomenclature) {
            notifyError(t('common.selectNomenclature'));
            return;
        }

        if (type === 'edit' && !selectedData) {
            notifyError(t('common.selectRecord'));
            return;
        }

        let editData = null;
        if (type === 'edit' && selectedData !== null) {
            const selectedItem = getSelectedItem();
            try {
                const detailedData = await nomenclaturesOz.getNomenclatureName(selectedItem.nomenclatureItem.id);
                editData = {
                    ...selectedItem,
                    nomenclatureItemLngs: detailedData
                };
            } catch (error) {
                console.error('Error fetching detailed data:', error);
                editData = selectedItem; // Fallback to basic data
            }
        }

        setModalState({ visible: true, type, editData });
    };

    const handleCloseModal = () => {
        setModalState({ visible: false, type: null, editData: null });
    };


    const handleConfirm = async (formData) => {
        try {
            if (modalState.type === 'add') {
                await nomenclaturesOz.createNomenclatureItem(formData);
                notifySuccess(t('common.createdSuccessfully'));
            } else if (modalState.type === 'edit') {
                const editData = getSelectedItem();
                await nomenclaturesOz.updateNomenclatureItem(editData.nomenclatureItem.id, formData);
                notifySuccess(t('common.updatedSuccessfully'));
            }

            handleCloseModal();
            if (selectedNomenclature) {
                await fetchNomenclatureItems();
            }
        } catch (error) {
            console.error('Error handling form submission:', error);
            notifyError(t('common.error'));
        }
    };

    const handleDeleteConfirm = async () => {
        try {
            const editData = getSelectedItem();

            await nomenclaturesOz.deleteNomenclatureItem(editData.nomenclatureItem.id);

            notifySuccess(t('common.deletedSuccessfully'));
            setSelectedData(null);

            handleCloseModal();

            if (selectedNomenclature) {
                await fetchNomenclatureItems();
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || t('common.error');
            console.error('Error deleting nomenclature item:', error);
            notifyError(errorMessage);
        }
    };

    const getSelectedItem = () => {
        return selectedData ? tableData.find(item => item.id === selectedData) : null;
    };

    const handleCheckboxChange = (index) => {
        const item = tableData[index];
        setSelectedData(selectedData === item.id ? null : item.id);
    };

    const searchOptions = [];

    const columns = [
        {
            key: 'code',
            label: t('organizationalStructure.nomenclatures.table.headers.code'),
        },
        {
            key: 'name',
            label: t('organizationalStructure.nomenclatures.table.headers.name'),
        },
        {
            key: 'isActive',
            label: t('organizationalStructure.nomenclatures.table.headers.active'),
        },
    ];

    const actionButtons = (
        <ActionButtons
            buttons={[
                { ...COMMON_BUTTONS.ADD, onClick: () => handleOpenModal('add') },
                { ...COMMON_BUTTONS.EDIT, onClick: () => handleOpenModal('edit') },
                { ...COMMON_BUTTONS.DELETE, onClick: () => handleOpenModal('delete') },
            ]}
            selectedRecord={selectedData}
        />
    );

    return (
        <>
            <Table
                title={t('organizationalStructure.nomenclatures.title')}
                columns={columns}
                data={tableData}
                loading={loading}
                error={error}
                actionButtons={actionButtons}
                showCustomSearch={false}
                searchOptions={searchOptions}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                handleSearchClick={() => { }}
                getData={() => { }}
                handleCheckboxChange={handleCheckboxChange}
                selectedData={selectedData}
                showPageSizeSelector={false}
                showPagination={false}
                showPreviousPage={false}
                showNomenclaturesSelect={true}
                showActiveSelect={true}
                selectedNomenclature={selectedNomenclature}
                setSelectedNomenclature={setSelectedNomenclature}
                nomenclaturesOptions={nomenclaturesOptions}
                selectedActive={selectedActive}
                setSelectedActive={setSelectedActive}
                renderCell={(item, column) => {
                    if (column.key === 'isActive') {
                        return item.isActive ? t('common.yes') : t('common.no');
                    }
                    return item[column.key];
                }}
            />
            {modalState.visible && modalState.type === 'add' && (
                <NomenclaturesOzForm
                    onClose={handleCloseModal}
                    editData={modalState.editData}
                    handleConfirm={handleConfirm}
                    selectedNomenclature={selectedNomenclature}
                />
            )}
            {modalState.visible && modalState.type === 'edit' && (
                <NomenclaturesOzForm
                    onClose={handleCloseModal}
                    editData={modalState.editData}
                    handleConfirm={handleConfirm}
                    selectedNomenclature={selectedNomenclature}
                />
            )}
            {modalState.visible && modalState.type === 'delete' && (
                <DeleteModal
                    onClose={handleCloseModal}
                    onConfirm={handleDeleteConfirm}
                    name={getSelectedItem()?.name || getSelectedItem()?.nameBg || ''}
                    entityType="record"
                />
            )}
        </>
    );
}